import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './CreditSummaryTable.module.scss';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { fetchCreditBureauSummaryTable } from '../../../../../../store/dashboard/creditBureau/actions';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SortIcon from '../../../../../Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon';
import { SortConfig } from '../../../../../../utils/constants/commonConstants';
import { DataTableColumn } from '../../../../../../common/_custom/DataTable/DataTable.types';
import {
    ACTIVE_ACCOUNT_STATUS,
    buildCreditSummaryColumns,
    CREDIT_SUMMARY_FILTER_SOURCE_KEY,
    CreditSummaryCellTone,
    dangerToneFor,
    CreditSummaryHeader,
    CreditSummaryRow,
    isSortableColumn,
    MISSING_CELL_VALUE,
} from './CreditSummaryTable.constants';
import useCreditSummaryRows, { CreditSummaryGroup, nextSortConfig, useCreditSummaryGeometry } from './useCreditSummaryRows';

const DEFAULT_EXPANDED_GROUP_INDEX = 0;

/** openStatus: null = nothing chosen yet (open the first group), this = every group collapsed. */
const ALL_COLLAPSED = '';

const HEAD_ROW_REM = 2.75;

const SECTION_BAR_REM = 2.75;

type Row = {
    name?: string;
    investorName?: string;
    loanType?: string;
    accountStatus?: string;
    lastReported?: string;
    sanctionDate?: string;
    sanctionedLimit?: string;
    outstandingAmount?: string;
    currentOverdue?: string;
    emi?: string;
    tenure?: string;
    frequency?: string;
    accountRemarks?: string;
    ownership?: string;
    repaymentHistory?: string;
    // Raw sanction date polus already serializes alongside the formatted string — preferred as the
    // sort key for that column so we never parse a display value we don't have to.
    sanctionedDateActual?: string;
};

type TableData = {
    headers: CreditSummaryHeader[];
    cibilData: Row[];
};

type CreditSummaryTableProps = {
    assesseeOrgId?: string;
    /**
     * Publishes the resolved filter columns to the parent, which owns the filter trigger and drawer
     * so they can sit in the report header beside the Chart/Table toggle. Applied filter VALUES are
     * deliberately not passed down — both sides read them from redux under
     * CREDIT_SUMMARY_FILTER_SOURCE_KEY, so there is no filter state to keep in sync.
     */
    onColumnsResolved?: (columns: DataTableColumn[]) => void;
};

function CreditSummaryTable({ assesseeOrgId, onColumnsResolved }: CreditSummaryTableProps) {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState<TableData | null>(null);
    const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);
    const [openStatus, setOpenStatus] = useState<string | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const directorId = searchParams.get('directorId');
    const bureauType = searchParams.get('bureauType') == 'All' ? null : searchParams.get('bureauType');

    const directorBureauType = searchParams.get('directorBureauType');

    const appliedFilters = useAppSelector((state) => state.investor.customFilterDrawer?.appliedFilters?.[CREDIT_SUMMARY_FILTER_SOURCE_KEY]);

    useEffect(() => {
        assesseeOrgId && fetchTableData(assesseeOrgId);
    }, [assesseeOrgId]);

    /* Takes the org id as an argument rather than closing over the optional prop, so the payload
       field is narrowed to a string at the call site's existing guard. */
    const fetchTableData = (orgId: string) => {
        setLoading(true);
        dispatch(
            fetchCreditBureauSummaryTable(
                {
                    assesseeOrgId: orgId,
                    ...(directorId && { directorId: directorId }),
                    ...((bureauType || directorBureauType) && { type: bureauType ?? directorBureauType }),
                },
                {
                    onSuccess: (data) => {
                        setLoading(false);
                        setTableData(data);
                        setOpenStatus(null);
                    },
                    onError: () => {
                        setLoading(false);
                    },
                },
            ),
        );
    };

    /* Filter fields are derived from the headers polus sent, with tick-list options read out of the
       rows themselves — so the drawer can never offer a column this table doesn't have, nor a value
       that matches no row. */
    const filterColumns = useMemo(
        () => buildCreditSummaryColumns(tableData?.headers, tableData?.cibilData || []),
        [tableData?.headers, tableData?.cibilData],
    );

    useEffect(() => {
        onColumnsResolved?.(filterColumns);
    }, [filterColumns, onColumnsResolved]);

    const { displayRows, groups } = useCreditSummaryRows(tableData?.cibilData, appliedFilters, sortConfig);

    const onSort = (field: string) => setSortConfig((current) => nextSortConfig(current, field));

    const { columnStyles, tableWidth } = useCreditSummaryGeometry(tableData?.headers, tableData?.cibilData);

    const renderCell = (row: CreditSummaryRow, header: CreditSummaryHeader, index: number, fallback = '', bottom?: string) => {
        const field = header?.key;
        const tone = dangerToneFor(row, field);
        const value = row?.[field] || fallback;
        const style = bottom === undefined ? columnStyles[index] : { ...columnStyles[index], bottom };

        return (
            <td
                key={field || index}
                title={value || undefined}
                style={style}
                className={clsx(styles.TableBodyRowItem, {
                    [styles.SecondColumn]: index === 1,
                    [styles.DangerCell]: tone === CreditSummaryCellTone.DANGER_WHEN_PRESENT,
                })}
            >
                {tone === CreditSummaryCellTone.DANGER_WHEN_POSITIVE ? <span className={styles.DangerChip}>{value}</span> : value}
            </td>
        );
    };

    const bottomOffset = (groupIndex: number) => `${(groups.length - 1 - groupIndex) * SECTION_BAR_REM}rem`;

    /**
     * Which section is open. `null` means nothing has been chosen yet, so the first one opens;
     * ALL_COLLAPSED means the user closed the open one.
     *
     * A filter can remove the status that was open — filter to Closed accounts while Active is
     * expanded and `openStatus` matches no group, which would collapse every section and show no
     * rows at all despite there being rows to show. Fall back to the first section that survived.
     */
    const expandedStatus = useMemo(() => {
        if (openStatus === ALL_COLLAPSED) return ALL_COLLAPSED;
        if (openStatus === null) return groups[DEFAULT_EXPANDED_GROUP_INDEX]?.status ?? ALL_COLLAPSED;

        return groups.some((group) => group.status === openStatus) ? openStatus : groups[0]?.status ?? ALL_COLLAPSED;
    }, [openStatus, groups]);

    const isGroupExpanded = (group: CreditSummaryGroup) => expandedStatus === group.status;

    const toggleGroup = (status: string, currentlyExpanded: boolean) => {
        setOpenStatus(currentlyExpanded ? ALL_COLLAPSED : status);
        if (!currentlyExpanded && scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    };

    const renderGroupHeaderRow = (group: CreditSummaryGroup, expanded: boolean, colSpan: number, groupIndex: number) => (
        <tr className={styles.GroupHeaderRow} key={`group-${group.status}`}>
            <td
                colSpan={colSpan}
                style={{ top: `${HEAD_ROW_REM}rem`, bottom: bottomOffset(groupIndex) }}
                className={clsx(styles.GroupHeaderCell, { [styles.GroupHeaderCell_Collapsed]: !expanded })}
            >
                <button
                    type="button"
                    className={styles.GroupHeaderContent}
                    onClick={() => toggleGroup(group.status, expanded)}
                    aria-expanded={expanded}
                >
                    <svg
                        className={clsx(styles.GroupChevron, { [styles.GroupChevron_Expanded]: expanded })}
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                        fill="none"
                    >
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className={styles.GroupTitle}>{group.status}</span>
                    <span
                        className={clsx(styles.GroupBadge, {
                            [styles.GroupBadge_Active]: group.status === ACTIVE_ACCOUNT_STATUS,
                            [styles.GroupBadge_Other]: group.status !== ACTIVE_ACCOUNT_STATUS,
                        })}
                    >
                        {group.rows.length} {group.rows.length === 1 ? 'Account' : 'Accounts'}
                    </span>
                </button>
            </td>
        </tr>
    );

    const renderGroupTotalRow = (group: CreditSummaryGroup, headers: CreditSummaryHeader[], groupIndex: number) => (
        <tr className={clsx(styles.TableBodyRow, styles.TotalRow)} key={`total-${group.status}`}>
            {headers.map((header, i) =>
                i === 0 ? (
                    <td
                        key={header?.key || i}
                        style={{ ...columnStyles[i], bottom: bottomOffset(groupIndex) }}
                        className={styles.TableBodyRowItem}
                    >
                        Total
                    </td>
                ) : (
                    renderCell(group.totals, header, i, MISSING_CELL_VALUE, bottomOffset(groupIndex))
                ),
            )}
        </tr>
    );

    return (
        <div className={styles.CreditSummaryTablePage}>
            <div className={styles.TableContainer} ref={scrollContainerRef}>
                {loading ? (
                    <div className={styles.Loading}>
                        <LoadingSpinner height="40px" color="#6021b3" />
                    </div>
                ) : (
                    <>
                        {tableData?.headers?.length && displayRows?.length ? (
                            <table className={styles.CreditSummaryTable} style={{ width: tableWidth }}>
                                <thead className={styles.TableHead}>
                                    <tr className={styles.TableHeadRow}>
                                        {tableData?.headers?.map((header, i) => (
                                            <td
                                                key={header?.key || i}
                                                style={columnStyles[i]}
                                                className={clsx(styles.TableHeadRowItem, {
                                                    [styles.SecondHeader]: i === 1,
                                                })}
                                            >
                                                {isSortableColumn(header?.key) ? (
                                                    <span className={styles.SortableHeader}>
                                                        {header?.name}
                                                        <SortIcon
                                                            field={header.key}
                                                            sortConfig={sortConfig}
                                                            onSort={onSort}
                                                            alwaysVisible
                                                        />
                                                    </span>
                                                ) : (
                                                    header?.name || 'no header'
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                </thead>

                                {groups.map((group, groupIndex) => {
                                    const headers = tableData?.headers || [];
                                    const expanded = isGroupExpanded(group);

                                    return (
                                        <tbody className={styles.TableBody} key={`section-${group.status}`}>
                                            {renderGroupHeaderRow(group, expanded, headers.length, groupIndex)}
                                            {expanded &&
                                                group.rows.map((row, rowIndex) => (
                                                    <tr className={styles.TableBodyRow} key={`${group.status}-${rowIndex}`}>
                                                        {headers.map((header, i) => renderCell(row, header, i))}
                                                    </tr>
                                                ))}
                                            {expanded && renderGroupTotalRow(group, headers, groupIndex)}
                                        </tbody>
                                    );
                                })}
                            </table>
                        ) : (
                            <div className={styles.NoData}>No Data Available</div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default CreditSummaryTable;
